import Link from "next/link";
import Image from "next/image";
import { VFC, memo } from "react";

import { useFollow } from "hooks/articles/useFollow";
import { signin } from "lib/signin";

import { CurrentUser } from "lib/type";

import { FaTags, FaRegEdit } from "react-icons/fa";
import { FiClock } from "react-icons/fi";
import { MdUpdate } from "react-icons/md";
import { RiUserAddFill } from "react-icons/ri";

type ArticleLinksProps = {
  isAuthChecking: boolean;
  author: CurrentUser;
  user: CurrentUser;
  articleID: string;
  createdAt: string;
  updateAt: string | null | undefined;
  tags: string[] | null | undefined;
};

const ArticleLinks: VFC<ArticleLinksProps> = ({
  isAuthChecking,
  author,
  user,
  articleID,
  tags,
  createdAt,
  updateAt,
}) => {
  const tagList = tags?.map((tag, index) => {
    return (
      <li key={index} className="leading-none">
        <Link href={`/tags/${tag}`} prefetch={false}>
          <a className="text-link dark:text-linkDark whitespace-nowrap underline">
            #{tag}
          </a>
        </Link>
      </li>
    );
  });

  const { isFollowChecking, isFollowing, changeFollow } = useFollow(
    author,
    user
  );

  return (
    <>
      <div className="relative z-10">
        <div className="mb-6 flex items-center">
          <p className="flex items-center">
            <FiClock />
            {createdAt}
          </p>
          {updateAt && (
            <p className="ml-2 flex items-center">
              <MdUpdate className="text-xl" />
              {updateAt}
            </p>
          )}
        </div>
        {tags && tags.length > 0 && (
          <ul className="mb-6 flex flex-wrap gap-2">
            <li className="leading-none">
              <FaTags />
            </li>
            {tagList}
          </ul>
        )}
      </div>
      {author && (
        <div className="md:flex md:items-center">
          {isAuthChecking && <div className="h-8" />}
          {!isAuthChecking && user?.uid !== author?.uid && (
            <Link href={`/users/${author.uid}`} prefetch={false}>
              <a className="inline-flex items-center">
                <div className="nextimg mr-1 h-8 w-8">
                  <Image
                    src={author.avatar || "/default.svg"}
                    alt={author.name}
                    width={50}
                    height={50}
                    objectFit="cover"
                    className="rounded-full"
                  />
                </div>
                <p>{author.name}</p>
              </a>
            </Link>
          )}
          {!isAuthChecking && user?.uid === author?.uid && (
            <Link href={`/articles/${articleID}/edit`}>
              <a className="mb-4 inline-flex items-center underline">
                <FaRegEdit className="mr-1" />
                記事を編集する
              </a>
            </Link>
          )}
          {!isAuthChecking && !isFollowChecking && !user && (
            <button
              onClick={signin}
              className="bg-dark text-light dark:bg-light dark:text-dark mt-4 flex items-center rounded-md px-4 py-2 md:mt-0 md:ml-8"
            >
              <RiUserAddFill />
              サインインしてフォローする
            </button>
          )}
          {!isAuthChecking &&
            !isFollowChecking &&
            user &&
            author &&
            user.uid !== author.uid && (
              <button
                onClick={changeFollow}
                className={`mt-4 flex items-center rounded-md px-4 py-2 ${
                  isFollowing
                    ? "border-dark dark:border-light border border-solid"
                    : "bg-dark text-light dark:bg-light dark:text-dark"
                } md:mt-0 md:ml-8`}
              >
                <RiUserAddFill />
                {isFollowing === true && "フォロー中"}
                {isFollowing === false && "フォローする"}
              </button>
            )}
        </div>
      )}
    </>
  );
};

export default memo(ArticleLinks);
